import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import {
  LineChart, // Usado para el gráfico de Facturación Mensual
  Line, // Usado para el gráfico de Facturación Mensual
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  BarChart,
} from "recharts";
import {
  Users,
  TrendingUp,
  DollarSign,
  Activity,
  User,
  Calendar,
  Search,
  Filter,
  ChevronDown,
  BarChart3,
  PieChart as PieChartIcon,
  Loader2,
  AlertCircle,
  CheckCircle2,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Equal,
  Briefcase,
  Archive,
} from "lucide-react";
import { LabelList } from "recharts";

// ✅ CONFIGURACIÓN ANTI-ZOOM OBLIGATORIA
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// ✅ COLORES DE VISTA KPI TRABAJADORES
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
  solid: "#2563eb",
  light: "#2563eb10",
  description: "Análisis de rendimiento del equipo",
});

// ✅ DROPDOWN PERSONALIZADO UNIFICADO
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "#8b5cf6";
          e.target.style.boxShadow = "0 0 0 3px rgba(139, 92, 246, 0.1)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#f1f5f9";
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
              onMouseEnter={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "#f8fafc";
                }
              }}
              onMouseLeave={(e) => {
                if (option.value !== value) {
                  e.target.style.background = "transparent";
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ✅ COMPONENTE TOGGLE PARA MONEDAS (Solo ARS y USD)
const CurrencyToggle = ({ value, onChange }) => {
  const colorVista = getVistaColor();

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: "#f8fafc",
        borderRadius: "12px",
        padding: "4px",
        border: "1px solid #e2e8f0",
      }}
    >
      {["ARS", "USD"].map((currency) => (
        <button
          key={currency}
          onClick={() => onChange(currency)}
          className="button-hover"
          style={{
            padding: "8px 16px",
            border: "none",
            borderRadius: "8px",
            background:
              value === currency ? colorVista.gradient : "transparent",
            color: value === currency ? "white" : "#64748b",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            minWidth: "60px",
            justifyContent: "center",
            touchAction: "manipulation",
          }}
        >
          <DollarSign size={12} />
          {currency}
        </button>
      ))}
    </div>
  );
};

// ✅ COMPONENTE PARA MOSTRAR TENDENCIAS CON INFORMACIÓN DETALLADA
const TrendIndicator = ({
  current,
  previous,
  showIcon = true,
  showPercentage = true,
  comparisonText = "",
  centerAlign = false,
}) => {
  if (previous === 0 || previous === null || previous === undefined) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: centerAlign ? "center" : "flex-start",
          gap: "4px",
          fontSize: "12px",
          fontWeight: "500",
          color: "#94a3b8",
          fontStyle: "italic",
        }}
      >
        Sin datos previos
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: centerAlign ? "center" : "flex-start",
        gap: "4px",
        fontSize: "12px",
        fontWeight: "600",
        color: "#64748b",
        flexDirection: "column",
      }}
    >
      {comparisonText && (
        <span
          style={{ fontSize: "10px", color: "#94a3b8", textAlign: "center" }}
        >
          {comparisonText}
        </span>
      )}
    </div>
  );
};

// ✅ GRÁFICO DE EVOLUCIÓN MENSUAL (VERSIÓN MEJORADA)
const EvolucionMensualChart = ({
  data,
  monedaVista,
  formatCurrency,
  isMobile,
}) => {
  // 1. Lógica para calcular el total mensual. No cambia.
  const datosConTotal = data.map((item) => ({
    ...item,
    totalMes: Object.keys(item)
      .filter(
        (key) => !["mes", "mesRaw", "promedio12Meses", "total"].includes(key)
      )
      .reduce((sum, key) => sum + (parseFloat(item[key]) || 0), 0),
  }));

  // 2. Formateador para el eje Y: Abrevia números grandes (ej: 1500K, 1.5M)
  const formatYAxis = (value) => {
    const symbol = monedaVista === "ARS" ? "$" : "US$";
    if (value >= 1000000) return `${symbol}${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${symbol}${(value / 1000).toFixed(0)}K`;
    return `${symbol}${value}`;
  };

  // 3. Intervalo dinámico para el eje X: Muestra menos etiquetas si hay muchos datos.
  const getTickInterval = () => {
    if (isMobile) {
      if (data.length > 12) return Math.floor(data.length / 4);
      if (data.length > 6) return 1;
    } else {
      if (data.length > 18) return Math.floor(data.length / 6);
      if (data.length > 12) return 1;
    }
    return 0; // Muestra todas las etiquetas si hay pocas.
  };

  // 4. Componente personalizado para el Tooltip (información al pasar el mouse)
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.find((p) => p.dataKey === "totalMes");
      const promedio = payload.find((p) => p.dataKey === "promedio12Meses");

      return (
        <div
          style={{
            background: "rgba(255, 255, 255, 0.9)",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            padding: "12px",
            fontSize: "13px",
            backdropFilter: "blur(4px)",
          }}
        >
          <p style={{ fontWeight: "600", color: "#1e293b", margin: 0 }}>
            {label}
          </p>
          <div style={{ marginTop: "8px" }}>
            {total && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "4px",
                }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "3px",
                    backgroundColor: "#8b5cf6",
                    opacity: 0.7,
                  }}
                />
                <span style={{ color: "#64748b" }}>Total Mes:</span>
                <span style={{ fontWeight: "600", color: "#374151" }}>
                  {formatCurrency(total.value)}
                </span>
              </div>
            )}
            {promedio && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "3px",
                    backgroundColor: "#7c3aed",
                  }}
                />
                <span style={{ color: "#64748b" }}>Promedio 12M:</span>
                <span style={{ fontWeight: "600", color: "#374151" }}>
                  {formatCurrency(promedio.value)}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  // 5. Renderizado del componente (HTML y Gráfico)
  return (
    <div style={{ padding: "0" }}>
      {/* --- Contenedor del Gráfico --- */}
      <div style={{ height: "320px" }}>
        {!data || data.length === 0 ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              background: "#f8fafc",
              borderRadius: "12px",
            }}
          >
            <BarChart3 size={40} style={{ marginBottom: "12px" }} />
            <span style={{ fontWeight: 500 }}>No hay datos para mostrar</span>
            <span style={{ fontSize: "12px", marginTop: "4px" }}>
              Selecciona otro período o filtros
            </span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={datosConTotal}
              margin={{ top: 5, right: 5, bottom: 5, left: -20 }}
              barCategoryGap="20%"
            >
              <defs>
                {/* Definición del gradiente para las barras */}
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                vertical={false}
              />

              <XAxis
                dataKey="mes"
                stroke="#9ca3af"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                interval={getTickInterval()}
              />

              <YAxis
                stroke="#9ca3af"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={formatYAxis}
                tickCount={6}
                domain={[0, "dataMax + dataMax * 0.1"]}
              />

              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "rgba(139, 92, 246, 0.08)" }}
              />

              <Line
                type="monotone"
                dataKey="promedio12Meses"
                stroke="#7c3aed"
                strokeWidth={3}
                dot={false}
                activeDot={{
                  r: 6,
                  strokeWidth: 2,
                  stroke: "#7c3aed",
                  fill: "white",
                }}
                name="Promedio últimos 12 meses"
              />

              <Bar
                dataKey="totalMes"
                fill="url(#colorTotal)"
                radius={[8, 8, 0, 0]}
                name="Total mensual"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

// ✅ NUEVO: GRÁFICO DE EQUIDAD DE SOCIOS CORREGIDO
const EquidadSociosChart = ({ datosEquidad, formatCurrency, isMobile }) => {
  if (!datosEquidad || datosEquidad.socios.length === 0) {
    return (
      <div
        style={{
          height: "300px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          fontSize: "14px",
          background: "#f8fafc", // Consistent with other no-data states
          borderRadius: "12px", // Consistent with other no-data states
        }}
      >
        <Equal size={40} style={{ marginBottom: "12px", opacity: 0.5 }} />{" "}
        {/* Changed icon to Equal */}
        <h4
          style={{
            fontWeight: "600",
            color: "#374151",
            marginBottom: "4px",
            fontSize: "14px",
          }}
        >
          Sin datos de socios
        </h4>
        <p
          style={{
            margin: 0,
            textAlign: "center",
            lineHeight: "1.4",
            fontSize: "12px",
          }}
        >
          No hay información de socios para analizar equidad
        </p>
      </div>
    );
  }

  const {
    socios,
    promedioEquitativo,
    totalSocios,
    coeficienteVariacion,
    periodoAnalisis,
    socioQueMasCobro,
    socioQueMenosCobro,
    diferenciaParaIgualar,
  } = datosEquidad;

  return (
    <div style={{ height: "300px" }}>
      {/* Indicador de equidad */}
      <div
        style={{
          background: coeficienteVariacion < 30 ? "#dcfce7" : "#fef2f2",
          border: `1px solid ${
            coeficienteVariacion < 30 ? "#16a34a" : "#ef4444"
          }`,
          borderRadius: "8px",
          padding: "12px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {coeficienteVariacion < 30 ? (
          <CheckCircle2 size={16} color="#16a34a" />
        ) : (
          <AlertCircle size={16} color="#ef4444" />
        )}
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: coeficienteVariacion < 30 ? "#16a34a" : "#ef4444",
              marginBottom: "2px",
            }}
          >
            {coeficienteVariacion < 30
              ? "Distribución Equitativa"
              : "Distribución Desigual"}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            {periodoAnalisis} | Variación: {coeficienteVariacion.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Lista de socios con comparación */}
      <div
        style={{
          maxHeight: "160px",
          overflowY: "auto",
          display: "grid",
          gap: "8px",
          marginBottom: "12px",
        }}
      >
        {socios.map((socio, index) => {
          const diferencia = socio.totalCobrado - promedioEquitativo;
          const necesitaAjuste =
            Math.abs(diferencia) > promedioEquitativo * 0.05; // 5% de tolerancia
          const porcentajeTotal = (socio.totalCobrado / totalSocios) * 100;

          return (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px",
                background: "#f8fafc",
                borderRadius: "8px",
                border: necesitaAjuste
                  ? `1px solid ${diferencia > 0 ? "#fbbf24" : "#ef4444"}20`
                  : "1px solid #e2e8f0",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1e293b",
                    marginBottom: "4px",
                  }}
                >
                  {socio.nombre}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {porcentajeTotal.toFixed(1)}% del total
                </div>
              </div>

              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    marginBottom: "2px",
                  }}
                >
                  {formatCurrency(socio.totalCobrado)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ✅ NUEVO: Mensaje de igualación */}
      {diferenciaParaIgualar > 0 && (
        <div
          style={{
            background: "#fef3c7",
            border: "1px solid #fbbf24",
            borderRadius: "8px",
            padding: "12px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "#92400e",
              marginBottom: "4px",
            }}
          >
            Para estar igualados
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#92400e",
              lineHeight: "1.4",
            }}
          >
            {socioQueMenosCobro.nombre} debería cobrar{" "}
            <span style={{ fontWeight: "600" }}>
              {formatCurrency(diferenciaParaIgualar)}
            </span>{" "}
            adicional
          </div>
        </div>
      )}
    </div>
  );
};

// ✅ MODIFICADO: GRÁFICO YTD HISTÓRICO (BARRAS HORIZONTALES)
const YTDHistoricoChart = ({ datosYTD, formatCurrency, isMobile }) => {
  // 1. Formateador para abreviar números (ej: 5.1M, 950.5K, 0.3K)
  const abbreviateValue = (value) => {
    if (value === 0) return "0";
    const absValue = Math.abs(value);
    if (absValue >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`.replace(".0", "");
    }
    // Para valores >= 1000 y < 1M, y también < 1000
    if (absValue > 0) {
      return `${(value / 1000).toFixed(1)}K`.replace(".0", "");
    }
    return value.toString();
  };

  // 2. Componente para la etiqueta personalizada que va A LA DERECHA de cada barra
  const CustomBarLabel = ({ x, y, width, height, value }) => {
    // Posicionar la etiqueta a la derecha de la barra
    return (
      <text
        x={x + width + 5} // 5px de espacio desde el final de la barra
        y={y + height / 2} // Centrado verticalmente en la barra
        dy=".35em" // Ajuste fino vertical
        fill="#475569"
        fontSize={12}
        fontWeight="500"
        textAnchor="start" // Alinear al inicio del texto
      >
        {abbreviateValue(value)}
      </text>
    );
  };

  // 3. Componente para el Tooltip (sin cambios)
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: "rgba(255, 255, 255, 0.9)",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            padding: "12px",
            fontSize: "13px",
            backdropFilter: "blur(4px)",
          }}
        >
          <p
            style={{ fontWeight: "600", color: "#1e293b", margin: "0 0 6px 0" }}
          >
            Año: {label}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                width: "10px",
                height: "10px",
                borderRadius: "3px",
                backgroundColor: "#8b5cf6",
              }}
            />
            <span style={{ color: "#64748b" }}>Total YTD:</span>
            <span style={{ fontWeight: "600", color: "#374151" }}>
              {formatCurrency(payload[0].value)}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  // 4. Lógica para el estado sin datos
  const noHayDatos =
    !datosYTD ||
    datosYTD.length === 0 ||
    datosYTD.every((item) => !item.total || item.total === 0);

  // 5. Renderizado del componente (sin el h3 y p internos para evitar duplicados)
  return (
    <div style={{ height: "300px" }}>
      {noHayDatos ? (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#94a3b8",
            background: "#f8fafc",
            borderRadius: "12px",
          }}
        >
          <Archive size={40} style={{ marginBottom: "12px" }} />
          <span style={{ fontWeight: 500 }}>No hay datos históricos</span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical" // <-- EJE PRINCIPAL: BARRAS HORIZONTALES
            data={datosYTD}
            margin={{ top: 5, right: 60, left: 10, bottom: 5 }} // Margen a la derecha para las etiquetas
          >
            <defs>
              <linearGradient
                id="ytdGradientHorizontal"
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.9} />
                <stop offset="100%" stopColor="#8b5cf6" stopOpacity={1} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              horizontal={false}
            />

            <XAxis type="number" hide={true} domain={[0, "dataMax * 1.2"]} />
            <YAxis
              type="category"
              dataKey="año"
              stroke="#9ca3af"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              width={40} // Espacio para las etiquetas de año
              dx={-5}
            />

            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(139, 92, 246, 0.08)" }}
            />

            <Bar
              dataKey="total"
              fill="url(#ytdGradientHorizontal)"
              radius={[0, 8, 8, 0]} // Radios para barra horizontal
              name="Total YTD"
            >
              <LabelList dataKey="total" content={<CustomBarLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

// ✅ MODIFICADO: GRÁFICO DE FACTURACIÓN MENSUAL (PORCENTAJES)
const FacturacionMensualChart = ({
  datosFacturacion,
  formatCurrency,
  isMobile,
}) => {
  // 1. Calcular el total facturado en el período para sacar porcentajes
  const totalFacturadoPeriodo = datosFacturacion.reduce(
    (sum, item) => sum + item.facturado,
    0
  );

  // 2. Añadir el porcentaje a cada item de los datos
  const datosConPorcentaje = datosFacturacion.map((item) => ({
    ...item,
    porcentaje:
      totalFacturadoPeriodo > 0
        ? (item.facturado / totalFacturadoPeriodo) * 100
        : 0,
  }));

  // 3. Componente para el Tooltip (información al pasar el mouse) - Adaptado para línea
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload; // Datos completos del mes
      return (
        <div
          style={{
            background: "rgba(255, 255, 255, 0.9)",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            padding: "12px",
            fontSize: "13px",
            backdropFilter: "blur(4px)",
          }}
        >
          <p
            style={{ fontWeight: "600", color: "#1e293b", margin: "0 0 8px 0" }}
          >
            {label}
          </p>
          <div style={{ display: "grid", gap: "4px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "3px",
                  backgroundColor: "#10b981", // Color de la línea
                }}
              />
              <span style={{ color: "#64748b" }}>Facturado:</span>
              <span style={{ fontWeight: "600", color: "#374151" }}>
                {formatCurrency(data.facturado)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "3px",
                  backgroundColor: "#10b981",
                  opacity: 0.6,
                }}
              />
              <span style={{ color: "#64748b" }}>Porcentaje:</span>
              <span style={{ fontWeight: "600", color: "#374151" }}>
                {`${data.porcentaje.toFixed(1)}% del total`}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // 4. Lógica para el estado sin datos
  if (!datosFacturacion || datosFacturacion.length === 0) {
    return (
      <div
        style={{
          height: "300px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          fontSize: "14px",
          background: "#f8fafc",
          borderRadius: "12px",
        }}
      >
        <BarChart3 size={40} style={{ marginBottom: "12px", opacity: 0.5 }} />
        <h4
          style={{
            fontWeight: "600",
            color: "#374151",
            marginBottom: "4px",
            fontSize: "14px",
          }}
        >
          Sin datos de facturación
        </h4>
        <p
          style={{
            margin: 0,
            textAlign: "center",
            lineHeight: "1.4",
            fontSize: "12px",
          }}
        >
          No hay información de facturación para analizar
        </p>
      </div>
    );
  }

  // New: Dynamic interval for X-axis
  const getTickIntervalFacturacion = () => {
    const numDataPoints = datosFacturacion.length;
    if (numDataPoints > 24) {
      return Math.floor(numDataPoints / 6); // Show about 6 ticks
    }
    if (numDataPoints > 12) {
      return Math.floor(numDataPoints / 4); // Show about 4 ticks
    }
    if (numDataPoints > 6) {
      return 1; // Show every other tick
    }
    return 0; // Show all ticks if few data points
  };

  // 5. Renderizado del nuevo gráfico (LineChart)
  return (
    <div style={{ height: "300px" }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart // Changed from BarChart to LineChart
          data={datosConPorcentaje}
          margin={{ top: 30, right: 10, left: -10, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e5e7eb"
            vertical={false}
          />

          <XAxis
            dataKey="mes"
            stroke="#9ca3af"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            interval={getTickIntervalFacturacion()} // Dynamic interval
          />

          <YAxis
            stroke="#9ca3af"
            fontSize={11}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => `${value.toFixed(0)}%`}
            domain={[0, "dataMax + 10"]}
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: "rgba(16, 185, 129, 0.2)", strokeWidth: 2 }} // Cursor para línea
          />

          <Line
            type="monotone" // Super curva
            dataKey="porcentaje"
            stroke="#10b981" // Color de la línea
            strokeWidth={3} // Línea más gruesa
            dot={false} // No mostrar puntos individuales
            activeDot={{
              r: 6,
              strokeWidth: 2,
              stroke: "#10b981",
              fill: "white",
            }} // Punto activo al pasar el mouse
            name="Porcentaje facturado"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// ✅ COMPONENTE PRINCIPAL KPI TRABAJADORES CORREGIDO
function KPITrabajadores({ constructora, usuario, permisosCliente }) {
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [monedaVista, setMonedaVista] = useState("USD"); // Solo ARS o USD

  // Estados para datos
  const [trabajadores, setTrabajadores] = useState([]);
  const [cobros, setCobros] = useState([]);
  const [evolucionMensual, setEvolucionMensual] = useState([]);
  const [evolucionMensualHistorica, setEvolucionMensualHistorica] = useState(
    []
  );
  const [datosEquidad, setDatosEquidad] = useState(null);
  const [datosYTDHistorico, setDatosYTDHistorico] = useState([]);
  const [datosFacturacion, setDatosFacturacion] = useState([]);
  const [kpiRenovados, setKpiRenovados] = useState({});

  // Estados para filtros (MANTENIDOS + AÑOS)
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTrabajador, setFilterTrabajador] = useState("todos");
  const [filterTipo, setFilterTipo] = useState("todos");
  const [filterPeriodo, setFilterPeriodo] = useState("999"); // Todo el período
  const [filterAño, setFilterAño] = useState("todos"); // Todos los años
  const [añosDisponibles, setAñosDisponibles] = useState([]);

  // Estados para cotización
  const [cotizacionDolar, setCotizacionDolar] = useState(null);

  const colorVista = getVistaColor();

  // ✅ SCROLL RESET OBLIGATORIO
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ✅ ESCAPE handler obligatorio
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        // KPI Trabajadores no tiene modales, pero mantenemos consistencia
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id) {
      obtenerCotizacionDolar();
      cargarDatos();
    }
  }, [constructora]);

  useEffect(() => {
    if (cobros.length > 0 && trabajadores.length > 0 && cotizacionDolar) {
      procesarDatos();
    }
  }, [
    cobros,
    trabajadores,
    monedaVista,
    filterTrabajador,
    filterTipo,
    filterPeriodo,
    filterAño,
    cotizacionDolar,
  ]);

  // ✅ FUNCIÓN: Obtener cotización del dólar
  const obtenerCotizacionDolar = async () => {
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      setCotizacionDolar(data.venta);
      console.log("💰 Dólar oficial cargado:", data.venta);
    } catch (error) {
      console.error("Error cargando dólar oficial:", error);
      setCotizacionDolar(1000); // Fallback
    }
  };

  // ✅ FUNCIÓN: Cargar datos de trabajadores y cobros
  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar trabajadores activos
      const { data: trabajadoresData, error: errorTrabajadores } =
        await supabase
          .from("trabajadores")
          .select("id, nombre, apellido, tipo, puesto")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .is('deleted_at', null)
          .order("apellido");

      if (errorTrabajadores) {
        console.error("Error cargando trabajadores:", errorTrabajadores);
      } else {
        setTrabajadores(trabajadoresData || []);
      }

      // Cargar cobros con información relacionada
      const { data: cobrosData, error: errorCobros } = await supabase
        .from("cobros_trabajadores")
        .select(
          `
          *,
          trabajadores!inner(nombre, apellido, tipo, puesto),
          conceptos_cobro(nombre)
        `
        )
        .eq("constructora_id", constructora.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .order("fecha", { ascending: true });

      if (errorCobros) {
        console.error("Error cargando cobros:", errorCobros);
      } else {
        setCobros(cobrosData || []);

        // Calcular años disponibles usando substring para evitar problemas de zona horaria
        const años = [
          ...new Set(
            cobrosData?.map((c) => c.fecha && c.fecha.substring(0, 4)) || []
          ),
        ].sort((a, b) => b - a);
        setAñosDisponibles(años);
        console.log("📅 Años disponibles:", años);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  // ✅ FUNCIÓN: Convertir monto entre monedas
  const convertirMonto = (
    monto,
    monedaOriginal,
    monedaDestino,
    tipoCambio = null
  ) => {
    const montoNum = parseFloat(monto || 0);

    if (monedaOriginal === monedaDestino) {
      return montoNum;
    }

    const tcNum = parseFloat(tipoCambio || cotizacionDolar || 1000);

    if (monedaDestino === "ARS" && monedaOriginal === "USD") {
      return montoNum * tcNum;
    }

    if (monedaDestino === "USD" && monedaOriginal === "ARS") {
      return montoNum / tcNum;
    }

    return montoNum;
  };

  // ✅ FUNCIÓN: Formatear moneda
  const formatCurrency = (valor, moneda = null) => {
    const monedaFinal = moneda || monedaVista;
    const simbolo = monedaFinal === "ARS" ? "$" : "US$";
    return `${simbolo}${valor.toLocaleString("es-AR", {
      maximumFractionDigits: 0,
    })}`;
  };

  // ✅ FUNCIÓN: Procesar datos para KPIs y gráficos CORREGIDOS
  const procesarDatos = () => {
    console.log("📊 PROCESANDO DATOS KPI TRABAJADORES CORREGIDOS...");

    // Filtrar cobros según criterios
    let cobrosFiltrados = cobros;

    // Filtro por trabajador
    if (filterTrabajador !== "todos") {
      cobrosFiltrados = cobrosFiltrados.filter(
        (c) => c.trabajador_id === filterTrabajador
      );
    }

    // Filtro por tipo (socio/empleado)
    if (filterTipo !== "todos") {
      cobrosFiltrados = cobrosFiltrados.filter(
        (c) => c.trabajadores.tipo === filterTipo
      );
    }

    // Filtro por período (solo si no hay filtro de año específico)
    if (filterAño === "todos" && filterPeriodo !== "999") {
      const fechaLimite = new Date();
      fechaLimite.setMonth(fechaLimite.getMonth() - parseInt(filterPeriodo));
      cobrosFiltrados = cobrosFiltrados.filter(
        (c) => new Date(c.fecha) >= fechaLimite
      );
    }

    // Filtro por año específico (usando substring para evitar problemas de zona horaria)
    if (filterAño !== "todos") {
      cobrosFiltrados = cobrosFiltrados.filter(
        (c) => c.fecha && c.fecha.substring(0, 4) === filterAño
      );
    }

    // Procesar evolución mensual con promedio de últimos 12 meses
    procesarEvolucionMensualRenovado(cobrosFiltrados);

    // Procesar equidad de socios CORREGIDA
    procesarEquidadSociosCorregida(cobrosFiltrados);

    // Procesar YTD histórico CORREGIDO
    procesarYTDHistoricoCorregido(cobros); // YTD usa todos los cobros para comparar años completos

    // Procesar facturación mensual
    procesarFacturacionMensual(cobrosFiltrados);

    // Calcular KPIs renovados
    calcularKPIsRenovados(cobrosFiltrados);
  };

  // ✅ FUNCIÓN: Procesar evolución mensual con promedio real de últimos 12 meses
  const procesarEvolucionMensualRenovado = (cobrosData) => {
    // Datos filtrados para las barras (totales mensuales)
    const datosPorMesFiltrados = {};

    cobrosData.forEach((cobro) => {
      const mes = cobro.fecha.substring(0, 7); // YYYY-MM
      const año = cobro.fecha.substring(0, 4);
      if (filterAño !== "todos" && año !== filterAño) return; // Solo meses del año filtrado
      const trabajadorNombre = `${cobro.trabajadores.apellido}, ${cobro.trabajadores.nombre}`;

      // Convertir monto según moneda vista
      const montoConvertido = convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );

      if (!datosPorMesFiltrados[mes]) {
        datosPorMesFiltrados[mes] = {};
      }

      if (!datosPorMesFiltrados[mes][trabajadorNombre]) {
        datosPorMesFiltrados[mes][trabajadorNombre] = 0;
      }

      datosPorMesFiltrados[mes][trabajadorNombre] += montoConvertido;
    });

    // Datos históricos para el promedio móvil (respetando filtro de trabajador pero no otros filtros)
    const datosPorMesHistoricos = {};

    // Aplicar solo el filtro de trabajador para el promedio móvil
    let cobrosParaPromedio = cobros;
    if (filterTrabajador !== "todos") {
      cobrosParaPromedio = cobros.filter(
        (c) => c.trabajador_id === filterTrabajador
      );
    }

    cobrosParaPromedio.forEach((cobro) => {
      const mes = cobro.fecha.substring(0, 7); // YYYY-MM
      const año = cobro.fecha.substring(0, 4);
      if (filterAño !== "todos" && año !== filterAño) return; // Solo meses del año filtrado
      const trabajadorNombre = `${cobro.trabajadores.apellido}, ${cobro.trabajadores.nombre}`;

      // Convertir monto según moneda vista
      const montoConvertido = convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );

      if (!datosPorMesHistoricos[mes]) {
        datosPorMesHistoricos[mes] = {};
      }

      if (!datosPorMesHistoricos[mes][trabajadorNombre]) {
        datosPorMesHistoricos[mes][trabajadorNombre] = 0;
      }

      datosPorMesHistoricos[mes][trabajadorNombre] += montoConvertido;
    });

    // Convertir a array y ordenar por mes (usando datos filtrados para las barras)
    const mesesOrdenados = Object.keys(datosPorMesFiltrados)
      .filter(
        (mes) => filterAño === "todos" || mes.substring(0, 4) === filterAño
      )
      .sort();
    const evolucion = mesesOrdenados.map((mes, index) => {
      const datos = datosPorMesFiltrados[mes];
      // ✅ CORREGIDO: Usar substring en vez de new Date para evitar problemas de zona horaria
      const año = mes.substring(0, 4);
      const mesNum = parseInt(mes.substring(5, 7));
      const mesCorto = new Date(2000, mesNum - 1, 1).toLocaleDateString(
        "es-ES",
        { month: "short" }
      );
      const añoCorto = año.substring(2, 4);
      const mesFormateado = `${mesCorto} ${añoCorto}`;

      // Calcular promedio de últimos 12 meses usando datos históricos del trabajador filtrado
      const todosLosMesesHistoricos = Object.keys(datosPorMesHistoricos)
        .filter((m) => filterAño === "todos" || m.substring(0, 4) === filterAño)
        .sort();
      const indiceMesActual = todosLosMesesHistoricos.indexOf(mes);
      const mesesParaPromedio = todosLosMesesHistoricos.slice(
        Math.max(0, indiceMesActual - 11),
        indiceMesActual + 1
      );
      const totalParaPromedio = mesesParaPromedio.reduce((sum, mesPromedio) => {
        const totalMes = Object.values(
          datosPorMesHistoricos[mesPromedio] || {}
        ).reduce((s, v) => s + v, 0);
        return sum + totalMes;
      }, 0);
      const promedio12Meses = totalParaPromedio / mesesParaPromedio.length;
      return {
        mes: mesFormateado,
        mesRaw: mes,
        promedio12Meses: promedio12Meses,
        ...datos,
      };
    });

    setEvolucionMensual(evolucion);

    // Generar también la evolución histórica respetando el filtro de año
    const generarEvolucionMensualHistorica = () => {
      // Usar todos los cobros (sin filtros de tipo ni período, pero sí de año si está activo)
      const datosPorMesHistoricos = {};
      let cobrosParaPromedio = cobros;
      if (filterTrabajador !== "todos") {
        cobrosParaPromedio = cobros.filter(
          (c) => c.trabajador_id === filterTrabajador
        );
      }
      cobrosParaPromedio.forEach((cobro) => {
        const mes = cobro.fecha.substring(0, 7);
        const año = cobro.fecha.substring(0, 4);
        if (filterAño !== "todos" && año !== filterAño) return;
        const trabajadorNombre = `${cobro.trabajadores.apellido}, ${cobro.trabajadores.nombre}`;
        const montoConvertido = convertirMonto(
          cobro.monto,
          cobro.moneda,
          monedaVista,
          cobro.tipo_cambio
        );
        if (!datosPorMesHistoricos[mes]) {
          datosPorMesHistoricos[mes] = {};
        }
        if (!datosPorMesHistoricos[mes][trabajadorNombre]) {
          datosPorMesHistoricos[mes][trabajadorNombre] = 0;
        }
        datosPorMesHistoricos[mes][trabajadorNombre] += montoConvertido;
      });
      const mesesOrdenados = Object.keys(datosPorMesHistoricos)
        .filter(
          (mes) => filterAño === "todos" || mes.substring(0, 4) === filterAño
        )
        .sort();
      const evolucion = mesesOrdenados.map((mes, index) => {
        const datos = datosPorMesHistoricos[mes];
        // ✅ CORREGIDO: Usar substring en vez de new Date para evitar problemas de zona horaria
        const año = mes.substring(0, 4);
        const mesNum = parseInt(mes.substring(5, 7));
        const mesCorto = new Date(2000, mesNum - 1, 1).toLocaleDateString(
          "es-ES",
          { month: "short" }
        );
        const añoCorto = año.substring(2, 4);
        const mesFormateado = `${mesCorto} ${añoCorto}`;
        const todosLosMesesHistoricos = Object.keys(datosPorMesHistoricos)
          .filter(
            (m) => filterAño === "todos" || m.substring(0, 4) === filterAño
          )
          .sort();
        const indiceMesActual = todosLosMesesHistoricos.indexOf(mes);
        const mesesParaPromedio = todosLosMesesHistoricos.slice(
          Math.max(0, indiceMesActual - 11),
          indiceMesActual + 1
        );
        const totalParaPromedio = mesesParaPromedio.reduce(
          (sum, mesPromedio) => {
            const totalMes = Object.values(
              datosPorMesHistoricos[mesPromedio] || {}
            ).reduce((s, v) => s + v, 0);
            return sum + totalMes;
          },
          0
        );
        const promedio12Meses = totalParaPromedio / mesesParaPromedio.length;
        return {
          mes: mesFormateado,
          mesRaw: mes,
          promedio12Meses: promedio12Meses,
          ...datos,
        };
      });
      setEvolucionMensualHistorica(evolucion);
    };

    // Llamar a generarEvolucionMensualHistorica() al final de procesarEvolucionMensualRenovado
    generarEvolucionMensualHistorica();
  };

  // ✅ FUNCIÓN: Procesar equidad de socios CORREGIDA
  const procesarEquidadSociosCorregida = (cobrosData) => {
    // Filtrar solo socios
    const cobrosSocios = cobrosData.filter(
      (c) => c.trabajadores.tipo === "socio"
    );

    if (cobrosSocios.length === 0) {
      setDatosEquidad(null);
      return;
    }

    // Obtener todos los socios únicos
    const sociosUnicos = [
      ...new Set(
        cobrosSocios.map(
          (c) => `${c.trabajadores.apellido}, ${c.trabajadores.nombre}`
        )
      ),
    ];

    if (sociosUnicos.length < 2) {
      setDatosEquidad(null);
      return;
    }

    // Encontrar el primer mes donde TODOS los socios cobraron juntos
    const cobrosPorMes = {};
    cobrosSocios.forEach((cobro) => {
      const mes = cobro.fecha.substring(0, 7);
      const socioNombre = `${cobro.trabajadores.apellido}, ${cobro.trabajadores.nombre}`;

      if (!cobrosPorMes[mes]) {
        cobrosPorMes[mes] = new Set();
      }
      cobrosPorMes[mes].add(socioNombre);
    });

    // Encontrar el primer mes donde todos cobraron
    const mesesOrdenados = Object.keys(cobrosPorMes).sort();
    let mesInicioEquidad = null;

    for (const mes of mesesOrdenados) {
      if (cobrosPorMes[mes].size === sociosUnicos.length) {
        mesInicioEquidad = mes;
        break;
      }
    }

    if (!mesInicioEquidad) {
      // Si nunca cobraron todos juntos, usar desde el primer mes del socio más tardío
      const primerCobroPorSocio = {};
      cobrosSocios.forEach((cobro) => {
        const socioNombre = `${cobro.trabajadores.apellido}, ${cobro.trabajadores.nombre}`;
        const fecha = cobro.fecha;

        if (
          !primerCobroPorSocio[socioNombre] ||
          fecha < primerCobroPorSocio[socioNombre]
        ) {
          primerCobroPorSocio[socioNombre] = fecha;
        }
      });

      // Tomar la fecha más tardía como inicio
      mesInicioEquidad = Object.values(primerCobroPorSocio)
        .sort()
        .pop()
        .substring(0, 7);
    }

    // Filtrar cobros desde el mes de inicio de equidad
    const cobrosDesdeInicio = cobrosSocios.filter(
      (c) => c.fecha.substring(0, 7) >= mesInicioEquidad
    );

    // Calcular totales por socio desde el período de equidad
    const sociosData = {};
    cobrosDesdeInicio.forEach((cobro) => {
      const socioNombre = `${cobro.trabajadores.apellido}, ${cobro.trabajadores.nombre}`;

      if (!sociosData[socioNombre]) {
        sociosData[socioNombre] = 0;
      }

      const montoConvertido = convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );

      sociosData[socioNombre] += montoConvertido;
    });

    const socios = Object.entries(sociosData).map(([nombre, total]) => ({
      nombre,
      totalCobrado: total,
    }));

    const totalSocios = socios.reduce((sum, s) => sum + s.totalCobrado, 0);
    const promedioEquitativo = totalSocios / socios.length;

    // Calcular coeficiente de variación
    const desviacion = Math.sqrt(
      socios.reduce(
        (sum, s) => sum + Math.pow(s.totalCobrado - promedioEquitativo, 2),
        0
      ) / socios.length
    );
    const coeficienteVariacion = (desviacion / promedioEquitativo) * 100;

    // ✅ NUEVO: Calcular diferencia para estar igualados
    const sociosOrdenados = socios.sort(
      (a, b) => b.totalCobrado - a.totalCobrado
    );
    const socioQueMasCobro = sociosOrdenados[0];
    const socioQueMenosCobro = sociosOrdenados[sociosOrdenados.length - 1];
    const diferenciaParaIgualar =
      socioQueMasCobro.totalCobrado - socioQueMenosCobro.totalCobrado;

    // ✅ NUEVO: Crear texto del período según filtros aplicados
    let periodoAnalisis = "";

    if (filterAño !== "todos") {
      // Filtro por año específico
      periodoAnalisis = `Año ${filterAño}`;
    } else if (filterPeriodo !== "999") {
      // Filtro por período
      const fechaLimite = new Date();
      fechaLimite.setMonth(fechaLimite.getMonth() - parseInt(filterPeriodo));
      const fechaInicio = fechaLimite.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
      });
      const fechaFin = new Date().toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
      });
      periodoAnalisis = `Desde ${fechaInicio} hasta ${fechaFin}`;
    } else {
      // Todo el período
      const fechaInicio = new Date(mesInicioEquidad + "-01").toLocaleDateString(
        "es-ES",
        {
          year: "numeric",
          month: "short",
        }
      );
      const fechaFin = new Date().toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
      });
      periodoAnalisis = `Desde ${fechaInicio} hasta ${fechaFin}`;
    }

    setDatosEquidad({
      socios: sociosOrdenados,
      promedioEquitativo,
      totalSocios,
      coeficienteVariacion,
      periodoAnalisis,
      // ✅ NUEVO: Datos para igualación
      socioQueMasCobro,
      socioQueMenosCobro,
      diferenciaParaIgualar,
    });
  };

  // ✅ FUNCIÓN: Procesar YTD histórico CORREGIDO
  const procesarYTDHistoricoCorregido = (todosLosCobros) => {
    // Usa todos los cobros, sin filtros, para una comparación histórica justa
    const fechaActual = new Date();
    const mesActual = fechaActual.getMonth() + 1; // 1-12
    const ytdPorAño = {};

    todosLosCobros.forEach((cobro) => {
      const año = parseInt(cobro.fecha.substring(0, 4));
      const mes = parseInt(cobro.fecha.substring(5, 7));

      // Solo contar hasta el mes actual para cada año para que la comparación sea YTD vs YTD
      if (mes <= mesActual) {
        if (!ytdPorAño[año]) {
          ytdPorAño[año] = 0;
        }
        const montoConvertido = convertirMonto(
          cobro.monto,
          cobro.moneda,
          monedaVista,
          cobro.tipo_cambio
        );
        ytdPorAño[año] += montoConvertido;
      }
    });

    const datosYTD = Object.entries(ytdPorAño)
      .map(([año, total]) => ({
        año: año.toString(),
        total,
      }))
      .sort((a, b) => parseInt(a.año) - parseInt(b.año)); // Ordenar de más antiguo a más reciente

    setDatosYTDHistorico(datosYTD);
  };

  // ✅ FUNCIÓN: Procesar facturación mensual
  const procesarFacturacionMensual = (cobrosData) => {
    const cobrosPorMes = {};
    const facturadosPorMes = {};

    // Sumar totales y facturados por mes
    cobrosData.forEach((cobro) => {
      const mes = cobro.fecha.substring(0, 7); // YYYY-MM
      // Total cobrado
      if (!cobrosPorMes[mes]) cobrosPorMes[mes] = 0;
      cobrosPorMes[mes] += convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );
      // Total facturado
      if (cobro.tiene_factura) {
        if (!facturadosPorMes[mes]) facturadosPorMes[mes] = 0;
        facturadosPorMes[mes] += convertirMonto(
          cobro.monto,
          cobro.moneda,
          monedaVista,
          cobro.tipo_cambio
        );
      }
    });

    const mesesOrdenados = Object.keys(cobrosPorMes).sort();
    const datosFacturacion = mesesOrdenados.map((mes) => {
      const totalCobrado = cobrosPorMes[mes] || 0;
      const totalFacturado = facturadosPorMes[mes] || 0;
      const porcentaje =
        totalCobrado > 0 ? (totalFacturado / totalCobrado) * 100 : 0;
      // Formateo de mes igual que antes
      const año = mes.substring(0, 4);
      const mesNum = parseInt(mes.substring(5, 7));
      const mesCorto = new Date(2000, mesNum - 1, 1).toLocaleDateString(
        "es-ES",
        { month: "short" }
      );
      const añoCorto = año.substring(2, 4);
      const mesFormateado = `${mesCorto} ${añoCorto}`;
      return {
        mes: mesFormateado,
        mesRaw: mes,
        facturado: totalFacturado,
        totalCobrado,
        porcentaje,
      };
    });

    setDatosFacturacion(datosFacturacion);
  };

  // ✅ FUNCIÓN: Calcular KPIs renovados CORREGIDOS
  const calcularKPIsRenovados = (cobrosData) => {
    // ✅ CORREGIDO: Usar substring en vez de new Date para evitar problemas de zona horaria
    const fechaActual = new Date().toISOString().substring(0, 7); // YYYY-MM
    const añoActual = fechaActual.substring(0, 4);
    const mesActual = parseInt(fechaActual.substring(5, 7));

    // 1. Ganancia total acumulada (según filtros) - SIN texto "según filtros"
    const gananciaTotal = cobrosData.reduce((sum, cobro) => {
      const monto = convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );
      return sum + monto;
    }, 0);

    // 2. Promedio últimos 12 meses vs 12 meses anteriores
    const fechaLimite12 = new Date();
    fechaLimite12.setMonth(fechaLimite12.getMonth() - 12);

    const fechaLimite24 = new Date();
    fechaLimite24.setMonth(fechaLimite24.getMonth() - 24);

    // Usar substring para año y mes en vez de new Date
    const cobrosUltimos12 = cobros.filter((c) => {
      const año = parseInt(c.fecha.substring(0, 4));
      const mes = parseInt(c.fecha.substring(5, 7));
      const fechaCobro = new Date(año, mes - 1, 1);
      return fechaCobro >= fechaLimite12;
    });
    const cobros12Anteriores = cobros.filter((c) => {
      const año = parseInt(c.fecha.substring(0, 4));
      const mes = parseInt(c.fecha.substring(5, 7));
      const fechaCobro = new Date(año, mes - 1, 1);
      return fechaCobro >= fechaLimite24 && fechaCobro < fechaLimite12;
    });

    const totalUltimos12 = cobrosUltimos12.reduce((sum, cobro) => {
      const monto = convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );
      return sum + monto;
    }, 0);

    const total12Anteriores = cobros12Anteriores.reduce((sum, cobro) => {
      const monto = convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );
      return sum + monto;
    }, 0);

    const promedioMensual12 = totalUltimos12 / 12;
    const promedioMensual12Anterior = total12Anteriores / 12;

    // 3. YTD actual vs YTD año anterior
    const cobrosYTDActual = cobros.filter((c) => {
      const año = parseInt(c.fecha.substring(0, 4));
      const mes = parseInt(c.fecha.substring(5, 7));
      return año === añoActual && mes <= mesActual;
    });

    const cobrosYTDAnterior = cobros.filter((c) => {
      const año = parseInt(c.fecha.substring(0, 4));
      const mes = parseInt(c.fecha.substring(5, 7));
      return año === añoActual - 1 && mes <= mesActual;
    });

    const ytdActual = cobrosYTDActual.reduce((sum, cobro) => {
      const monto = convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );
      return sum + monto;
    }, 0);

    const ytdAnterior = cobrosYTDAnterior.reduce((sum, cobro) => {
      const monto = convertirMonto(
        cobro.monto,
        cobro.moneda,
        monedaVista,
        cobro.tipo_cambio
      );
      return sum + monto;
    }, 0);

    // 4. Tendencia REAL: Promedio móvil del mes anterior vs 4 meses atrás
    let tendenciaPorcentual = 0;
    let promedioMovilActual = 0;
    let promedioMovilHace4 = 0;
    if (evolucionMensualHistorica && evolucionMensualHistorica.length >= 5) {
      const idxUltimo = evolucionMensualHistorica.length - 1;
      promedioMovilActual =
        evolucionMensualHistorica[idxUltimo].promedio12Meses;
      promedioMovilHace4 =
        evolucionMensualHistorica[idxUltimo - 4].promedio12Meses;
      if (promedioMovilHace4 !== 0) {
        tendenciaPorcentual =
          (promedioMovilActual / promedioMovilHace4 - 1) * 100;
      } else {
        tendenciaPorcentual = 0;
      }
    }

    setKpiRenovados({
      gananciaTotal,
      promedioMensual12,
      promedioMensual12Anterior,
      ytdActual,
      ytdAnterior,
      promedioMovilActual,
      promedioMovilHace4,
      cambioPorcentual: tendenciaPorcentual,
    });
  };

  // ✅ PREPARAR OPCIONES PARA DROPDOWNS (MANTENIDAS + AÑOS)
  const trabajadoresOptions = [
    { value: "todos", label: "Todos los trabajadores" },
    ...trabajadores.map((trabajador) => ({
      value: trabajador.id,
      label: `${trabajador.apellido}, ${trabajador.nombre} (${trabajador.tipo})`,
    })),
  ];

  const tipoOptions = [
    { value: "todos", label: "Todos los tipos" },
    { value: "socio", label: "Solo Socios" },
    { value: "empleado", label: "Solo Empleados" },
  ];

  const periodoOptions = [
    { value: "3", label: "Últimos 3 meses" },
    { value: "6", label: "Últimos 6 meses" },
    { value: "12", label: "Último año" },
    { value: "24", label: "Últimos 2 años" },
    { value: "999", label: "Todo el período" },
  ];

  const añoOptions = [
    { value: "todos", label: "Todos los años" },
    ...añosDisponibles.map((año) => ({
      value: año.toString(),
      label: año.toString(),
    })),
  ];

  // ✅ STATS DATA RENOVADAS CON INFORMACIÓN CENTRADA Y COMPARACIONES CLARAS
  const statsData = [
    {
      label: "Ganancia Total",
      value: loading ? "..." : formatCurrency(kpiRenovados.gananciaTotal || 0),
      icon: DollarSign,
      color: "#10b981",
      subtitle: "Total acumulado",
    },
    {
      label: "Promedio Mensual",
      value: loading
        ? "..."
        : formatCurrency(kpiRenovados.promedioMensual12 || 0),
      icon: TrendingUp,
      color: "#3b82f6",
      trend: {
        current: kpiRenovados.promedioMensual12 || 0,
        previous: kpiRenovados.promedioMensual12Anterior || 0,
      },
      comparisonText: "vs 12 meses anteriores",
    },
    {
      label: "YTD vs Anterior",
      value: loading ? "..." : formatCurrency(kpiRenovados.ytdActual || 0),
      icon: Activity,
      color: "#8b5cf6",
      trend: {
        current: kpiRenovados.ytdActual || 0,
        previous: kpiRenovados.ytdAnterior || 0,
      },
      comparisonText: "vs mismo período año anterior",
    },
    {
      label: "Tendencia",
      value: loading ? (
        "..."
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {(kpiRenovados.cambioPorcentual || 0) >= 0 ? (
            <TrendingUp size={18} color="#10b981" />
          ) : (
            <TrendingDown size={18} color="#ef4444" />
          )}
          <span
            style={{
              color:
                (kpiRenovados.cambioPorcentual || 0) >= 0
                  ? "#10b981"
                  : "#ef4444",
              fontWeight: 700,
              fontSize: "1.1em",
            }}
          >
            {`${Math.abs(kpiRenovados.cambioPorcentual || 0).toFixed(1)}%`}
          </span>
        </div>
      ),
      icon: TrendingUp,
      color: (kpiRenovados.cambioPorcentual || 0) >= 0 ? "#10b981" : "#ef4444",
      trend: {
        current: kpiRenovados.promedioMovilActual || 0,
        previous: kpiRenovados.promedioMovilHace4 || 0,
      },
      comparisonText: "promedio móvil actual vs hace 4 meses",
    },
  ];

  // Verificación de acceso - Solo empleados y socios
  if (usuario?.tipo_usuario === "cliente") {
    return (
      <div
        style={{
          padding: isMobile ? "30px 20px" : "50px 40px",
          textAlign: "center",
          background: "white",
          margin: "20px",
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "2px solid #fbbf24",
        }}
      >
        <div style={{ fontSize: "64px", marginBottom: "24px" }}>🔒</div>
        <h2
          style={{
            color: "#dc2626",
            marginBottom: "16px",
            fontSize: isMobile ? "20px" : "24px",
          }}
        >
          Acceso Restringido
        </h2>
        <div
          style={{
            background: "#fef3cd",
            padding: "20px",
            borderRadius: "12px",
            marginBottom: "20px",
            border: "1px solid #fbbf24",
          }}
        >
          <h3
            style={{
              color: "#92400e",
              margin: "0 0 12px 0",
              fontSize: "16px",
            }}
          >
            Vista KPI Trabajadores
          </h3>
          <p
            style={{
              color: "#92400e",
              margin: "0",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Solo para Empleados y Socios
          </p>
        </div>
        <p
          style={{
            color: "#64748b",
            fontSize: "15px",
            lineHeight: "1.5",
            marginBottom: "24px",
            maxWidth: "500px",
            margin: "0 auto 24px auto",
          }}
        >
          Esta vista contiene información interna de rendimiento y análisis del
          equipo que no está disponible para clientes.
        </p>
      </div>
    );
  }

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* ✅ HEADER UNIFICADO */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: colorVista.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                flexShrink: 0,
              }}
            >
              <Users size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                KPI Trabajadores
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Análisis renovado de rendimiento y equidad
              </p>
            </div>
          </div>

          {/* Toggle de moneda */}
          <CurrencyToggle value={monedaVista} onChange={setMonedaVista} />
        </div>

        {/* ✅ FILTROS UNIFICADOS (MANTENIDOS + AÑOS) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr 1fr 1fr",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar trabajador..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: "100%",
                padding: "14px 16px 14px 44px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: "16px",
                WebkitAppearance: "none",
                appearance: "none",
                background: "white",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#8b5cf6";
                e.target.style.boxShadow = "0 0 0 3px rgba(139, 92, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
              }}
            />
          </div>

          <CustomDropdown
            value={filterTrabajador}
            onChange={setFilterTrabajador}
            options={trabajadoresOptions}
            placeholder="Trabajador"
            icon={User}
          />

          <CustomDropdown
            value={filterTipo}
            onChange={setFilterTipo}
            options={tipoOptions}
            placeholder="Tipo"
            icon={Briefcase}
          />

          <CustomDropdown
            value={filterPeriodo}
            onChange={setFilterPeriodo}
            options={periodoOptions}
            placeholder="Período"
            icon={Calendar}
          />

          <CustomDropdown
            value={filterAño}
            onChange={setFilterAño}
            options={añoOptions}
            placeholder="Año"
            icon={Calendar}
          />
        </div>
      </div>

      {/* ✅ STATS RENOVADAS CON INFORMACIÓN CENTRADA (SOLO DESKTOP) */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "12px",
            marginBottom: "20px",
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {statsData.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "16px 12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "120px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: "center",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: "0 0 8px 0",
                    textAlign: "center",
                  }}
                >
                  {stat.value}
                </p>
                {stat.trend && (
                  <TrendIndicator
                    current={stat.trend.current}
                    previous={stat.trend.previous}
                    showIcon={true}
                    showPercentage={true}
                    comparisonText={stat.comparisonText}
                    centerAlign={true}
                  />
                )}
                {stat.subtitle && !stat.trend && (
                  <p
                    style={{
                      fontSize: "9px",
                      color: "#94a3b8",
                      margin: "0",
                      fontWeight: "500",
                      textAlign: "center",
                    }}
                  >
                    {stat.subtitle}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ GRÁFICOS PRINCIPALES RENOVADOS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
          gap: "20px",
          marginBottom: "24px",
          animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Evolución Mensual Renovada */}
        <div
          className="card-hover"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px",
            border: "1px solid #f1f5f9",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: `${colorVista.solid}10`,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BarChart3 size={16} color={colorVista.solid} />
            </div>
            <div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                Evolución Mensual
              </h3>
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  margin: "4px 0 0 0",
                }}
              >
                Líneas con promedio año móvil + barras con total por mes
              </p>
            </div>
          </div>

          {loading ? (
            <div
              style={{
                height: "400px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
              }}
            >
              <Loader2
                size={32}
                className="loading-pulse"
                style={{ marginBottom: "12px" }}
              />
              <p style={{ margin: 0 }}>Cargando evolución mensual...</p>
            </div>
          ) : (
            <EvolucionMensualChart
              data={evolucionMensual}
              monedaVista={monedaVista}
              formatCurrency={formatCurrency}
              isMobile={isMobile}
            />
          )}
        </div>

        {/* Equidad de Socios Corregida */}
        <div
          className="card-hover"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px",
            border: "1px solid #f1f5f9",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#f59e0b10",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Equal size={16} color="#f59e0b" />
            </div>
            <div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                Equidad de Socios
              </h3>
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  margin: "4px 0 0 0",
                }}
              >
                {datosEquidad?.periodoAnalisis || "Analizando equidad..."}
              </p>
            </div>
          </div>

          {loading ? (
            <div
              style={{
                height: "300px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
              }}
            >
              <Loader2
                size={32}
                className="loading-pulse"
                style={{ marginBottom: "12px" }}
              />
              <p style={{ margin: 0 }}>Calculando equidad...</p>
            </div>
          ) : (
            <EquidadSociosChart
              datosEquidad={datosEquidad}
              formatCurrency={formatCurrency}
              isMobile={isMobile}
            />
          )}
        </div>
      </div>

      {/* ✅ GRÁFICOS SECUNDARIOS RENOVADOS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: "20px",
          marginBottom: "24px",
          animation: "fadeInUp 0.9s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* YTD Histórico Corregido */}
        <div
          className="card-hover"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px",
            border: "1px solid #f1f5f9",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#3b82f610",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Archive size={16} color="#3b82f6" />
            </div>
            <div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                YTD Histórico
              </h3>
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  margin: "4px 0 0 0",
                }}
              >
                Comparación del total acumulado anual
              </p>
            </div>
          </div>

          {loading ? (
            <div
              style={{
                height: "300px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
              }}
            >
              <Loader2
                size={32}
                className="loading-pulse"
                style={{ marginBottom: "12px" }}
              />
              <p style={{ margin: 0 }}>Cargando histórico...</p>
            </div>
          ) : (
            <YTDHistoricoChart
              datosYTD={datosYTDHistorico}
              formatCurrency={formatCurrency}
              isMobile={isMobile}
            />
          )}
        </div>

        {/* Facturación Mensual */}
        <div
          className="card-hover"
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "20px",
            border: "1px solid #f1f5f9",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "#10b98110",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BarChart3 size={16} color="#10b981" />
            </div>
            <div>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  margin: 0,
                }}
              >
                Facturación Mensual
              </h3>
              <p
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  margin: "4px 0 0 0",
                }}
              >
                % del total facturado por mes
              </p>
            </div>
          </div>

          {loading ? (
            <div
              style={{
                height: "300px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
              }}
            >
              <Loader2
                size={32}
                className="loading-pulse"
                style={{ marginBottom: "12px" }}
              />
              <p style={{ margin: 0 }}>Calculando facturación...</p>
            </div>
          ) : (
            <FacturacionMensualChart
              datosFacturacion={datosFacturacion}
              formatCurrency={formatCurrency}
              isMobile={isMobile}
            />
          )}
        </div>
      </div>

      {/* ✅ CSS OBLIGATORIO - ANIMACIONES UNIFICADAS */}
      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .card-hover:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        /* Responsive desktop crítico */
        @media (min-width: 769px) {
          .header-section > div:first-child {
            flex-wrap: nowrap !important;
            min-height: 56px;
          }

          .button-hover {
            flex-shrink: 0 !important;
            white-space: nowrap !important;
            min-width: max-content !important;
          }

          .main-container {
            min-width: 1024px;
          }
        }

        /* Mejoras para gráficos Recharts */
        .recharts-wrapper {
          font-family: inherit;
        }

        .recharts-cartesian-axis-tick-value {
          font-weight: 500;
        }

        .recharts-tooltip-wrapper {
          outline: none;
        }

        .recharts-tooltip-content {
          background: white !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
          padding: 8px 12px !important;
          color: #1e293b !important;
          font-size: 12px !important;
        }

        .recharts-tooltip-label {
          color: #1e293b !important;
          font-weight: 600 !important;
          margin-bottom: 4px !important;
        }

        /* Efectos hover para elementos del gráfico */
        .recharts-line-dot:hover {
          r: 6 !important;
          transition: r 0.2s ease;
        }

        .recharts-bar:hover {
          filter: brightness(1.1);
          transition: filter 0.2s ease;
        }

        .recharts-pie-sector:hover {
          filter: brightness(1.1);
          transition: filter 0.2s ease;
        }

        /* Scrollbar personalizado */
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Mejoras para móvil */
        @media (max-width: 768px) {
          .recharts-cartesian-grid line {
            stroke-dasharray: 2 2;
            opacity: 0.5;
          }

          .card-hover:hover {
            transform: none;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
          }

          .recharts-cartesian-axis-tick-value {
            font-size: 10px;
          }
        }

        /* Animaciones escalonadas */
        .stat-card:nth-child(1) {
          animation-delay: 0.1s;
        }
        .stat-card:nth-child(2) {
          animation-delay: 0.2s;
        }
        .stat-card:nth-child(3) {
          animation-delay: 0.3s;
        }
        .stat-card:nth-child(4) {
          animation-delay: 0.4s;
        }

        .card-hover:nth-child(1) {
          animation-delay: 0.1s;
        }
        .card-hover:nth-child(2) {
          animation-delay: 0.2s;
        }

        /* Mejoras de accesibilidad */
        button:focus-visible {
          outline: 2px solid #8b5cf6;
          outline-offset: 2px;
        }

        .card-hover:focus-within {
          outline: 2px solid #8b5cf6;
          outline-offset: 2px;
        }

        /* Optimización para pantallas de alta resolución */
        @media (min-resolution: 2dppx) {
          .recharts-surface {
            shape-rendering: geometricPrecision;
          }
        }

        /* Efectos especiales para la equidad de socios */
        .equidad-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .equidad-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        /* Mejoras para las barras horizontales del YTD */
        .recharts-bar-rectangle:hover {
          filter: brightness(1.2);
          transition: filter 0.2s ease;
        }

        /* Responsive específico para gráficos complejos */
        @media (max-width: 1200px) {
          .recharts-cartesian-axis-tick-value {
            font-size: 9px;
          }
        }

        /* Centrado perfecto para stats */
        .stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
        }
      `}</style>
    </div>
  );
}

export default KPITrabajadores;
